import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { 
  Users, FileText, LogOut, ChevronRight, Mail, Calendar,
  Search, Download, Trash2, RefreshCw, MessageSquare, Bell
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { BecomeLogo } from "../components/BecomeLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sidebar Navigation Component
const AdminSidebar = ({ activeTab, setActiveTab, onLogout }) => {
  const navItems = [
    { id: "waitlist", label: "Waitlist", icon: Users },
    { id: "contacts", label: "Get in Touch", icon: MessageSquare },
    { id: "newsletter", label: "Stay Updated", icon: Bell },
    { id: "blog", label: "Blog Posts", icon: FileText },
  ];

  return (
    <div className="w-64 bg-[#1a1625] min-h-screen p-6 flex flex-col">
      <div className="mb-8">
        <Link to="/">
          <BecomeLogo size="small" />
        </Link>
        <p className="text-white/40 text-xs mt-2">Admin Dashboard</p>
      </div>

      <nav className="flex-1 space-y-2">
        {navItems.map((item) => (
          <button
            key={item.id}
            onClick={() => setActiveTab(item.id)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
              activeTab === item.id
                ? "bg-brand-peach/20 text-brand-peach"
                : "text-white/60 hover:bg-white/5 hover:text-white"
            }`}
            data-testid={`admin-nav-${item.id}`}
          >
            <item.icon className="w-5 h-5" />
            <span>{item.label}</span>
            {activeTab === item.id && <ChevronRight className="w-4 h-4 ml-auto" />}
          </button>
        ))}
      </nav>

      <button
        onClick={onLogout}
        className="flex items-center gap-3 px-4 py-3 text-white/60 hover:text-red-400 transition-colors"
        data-testid="admin-logout-btn"
      >
        <LogOut className="w-5 h-5" />
        <span>Logout</span>
      </button>
    </div>
  );
};

// Waitlist Management Component
const WaitlistPanel = ({ token }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchWaitlist = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/waitlist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data);
    } catch (error) {
      console.error("Error fetching waitlist:", error);
      toast.error("Failed to load waitlist");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWaitlist();
  }, [token]);

  const filteredEntries = entries.filter(entry =>
    entry.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const exportToCSV = () => {
    const headers = ["Name", "Email", "Interest", "ZIP Code", "Date"];
    const csvContent = [
      headers.join(","),
      ...entries.map(e => [
        `"${e.name || ''}"`,
        `"${e.email || ''}"`,
        `"${e.transition || ''}"`,
        `"${e.zip_code || ''}"`,
        `"${new Date(e.created_at).toLocaleDateString()}"`
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `become-waitlist-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Waitlist exported successfully");
  };

  const deleteEntry = async (id) => {
    if (!window.confirm("Are you sure you want to delete this entry?")) return;
    
    try {
      await axios.delete(`${API}/waitlist/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(entries.filter(e => e.id !== id));
      toast.success("Entry deleted");
    } catch (error) {
      toast.error("Failed to delete entry");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-heading text-white">Waitlist</h2>
          <p className="text-white/50 text-sm">{entries.length} total signups</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={fetchWaitlist}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="refresh-waitlist-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={exportToCSV}
            className="bg-brand-peach text-deep-violet hover:bg-brand-peach/90"
            data-testid="export-waitlist-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
        <Input
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40"
          data-testid="waitlist-search"
        />
      </div>

      {/* Table */}
      <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left px-6 py-4 text-white/60 font-medium text-sm">Name</th>
              <th className="text-left px-6 py-4 text-white/60 font-medium text-sm">Email</th>
              <th className="text-left px-6 py-4 text-white/60 font-medium text-sm">Interest</th>
              <th className="text-left px-6 py-4 text-white/60 font-medium text-sm">ZIP</th>
              <th className="text-left px-6 py-4 text-white/60 font-medium text-sm">Date</th>
              <th className="text-right px-6 py-4 text-white/60 font-medium text-sm">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-white/50">
                  Loading...
                </td>
              </tr>
            ) : filteredEntries.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-white/50">
                  {searchTerm ? "No matching entries found" : "No waitlist signups yet"}
                </td>
              </tr>
            ) : (
              filteredEntries.map((entry) => (
                <tr key={entry.id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="px-6 py-4 text-white">{entry.name || ""}</td>
                  <td className="px-6 py-4">
                    <a href={`mailto:${entry.email}`} className="text-brand-peach hover:underline flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {entry.email}
                    </a>
                  </td>
                  <td className="px-6 py-4 text-white/70">{entry.transition || ""}</td>
                  <td className="px-6 py-4 text-white/70">{entry.zip_code || ""}</td>
                  <td className="px-6 py-4 text-white/50 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {new Date(entry.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => deleteEntry(entry.id)}
                      className="text-white/40 hover:text-red-400 transition-colors"
                      data-testid={`delete-entry-${entry.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Contacts Panel, Get in Touch submissions
const ContactsPanel = ({ token }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/contact`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data);
    } catch (error) {
      console.error("Error fetching contacts:", error);
      toast.error("Failed to load contact submissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, [token]);

  const filteredEntries = entries.filter(entry =>
    entry.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.message?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const exportToCSV = () => {
    const headers = ["Name", "Email", "Subject", "Message", "Date"];
    const csvContent = [
      headers.join(","),
      ...entries.map(e => [
        `"${e.name || ''}"`,
        `"${e.email || ''}"`,
        `"${e.subject || ''}"`,
        `"${(e.message || '').replace(/"/g, '""')}"`,
        `"${new Date(e.created_at).toLocaleDateString()}"`
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `become-contacts-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Contacts exported successfully");
  };

  const deleteEntry = async (id) => {
    if (!window.confirm("Are you sure you want to delete this message?")) return;
    
    try {
      await axios.delete(`${API}/contact/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(entries.filter(e => e.id !== id));
      toast.success("Message deleted");
    } catch (error) {
      toast.error("Failed to delete message");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-heading text-white">Get in Touch</h2>
          <p className="text-white/50 text-sm">{entries.length} total messages</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={fetchContacts}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="refresh-contacts-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={exportToCSV}
            className="bg-brand-peach text-deep-violet hover:bg-brand-peach/90"
            data-testid="export-contacts-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
        <Input
          placeholder="Search by name, email, or message..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40"
          data-testid="contacts-search"
        />
      </div>

      {/* Messages List */}
      <div className="space-y-4">
        {loading ? (
          <div className="bg-white/5 rounded-xl border border-white/10 p-12 text-center text-white/50">
            Loading...
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="bg-white/5 rounded-xl border border-white/10 p-12 text-center text-white/50">
            {searchTerm ? "No matching messages found" : "No contact messages yet"}
          </div>
        ) : (
          filteredEntries.map((entry) => (
            <motion.div
              key={entry.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/5 rounded-xl border border-white/10 overflow-hidden"
            >
              {/* Header, Always visible */}
              <div 
                className="p-4 cursor-pointer hover:bg-white/5 transition-colors"
                onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-brand-peach/20 flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-brand-peach" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{entry.name || "Anonymous"}</p>
                      <a 
                        href={`mailto:${entry.email}`} 
                        className="text-brand-peach text-sm hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {entry.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-white/50 text-sm flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {new Date(entry.created_at).toLocaleDateString()}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteEntry(entry.id);
                      }}
                      className="text-white/40 hover:text-red-400 transition-colors p-2"
                      data-testid={`delete-contact-${entry.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {entry.subject && (
                  <p className="text-white/70 text-sm mt-2 ml-14">
                    Subject: {entry.subject}
                  </p>
                )}
              </div>
              
              {/* Expanded Message */}
              {expandedId === entry.id && (
                <div className="px-4 pb-4 ml-14 border-t border-white/10 pt-4">
                  <p className="text-white/80 whitespace-pre-wrap">{entry.message}</p>
                  <div className="mt-4 flex gap-2">
                    <a
                      href={`mailto:${entry.email}?subject=Re: ${entry.subject || 'Your message to Become'}`}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-brand-peach text-deep-violet rounded-lg text-sm font-medium hover:bg-brand-peach/90 transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                      Reply
                    </a>
                  </div>
                </div>
              )}
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

// Newsletter Panel, Stay Updated subscriptions
const NewsletterPanel = ({ token }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchNewsletter = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/newsletter`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data);
    } catch (error) {
      console.error("Error fetching newsletter subscriptions:", error);
      toast.error("Failed to load newsletter subscriptions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNewsletter();
  }, [token]);

  const filteredEntries = entries.filter(entry =>
    entry.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const exportToCSV = () => {
    const headers = ["Email", "Date"];
    const csvContent = [
      headers.join(","),
      ...entries.map(e => [
        `"${e.email || ''}"`,
        `"${new Date(e.created_at).toLocaleDateString()}"`
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `become-newsletter-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Newsletter list exported successfully");
  };

  const deleteEntry = async (id) => {
    if (!window.confirm("Are you sure you want to remove this subscriber?")) return;
    
    try {
      await axios.delete(`${API}/newsletter/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(entries.filter(e => e.id !== id));
      toast.success("Subscriber removed");
    } catch (error) {
      toast.error("Failed to remove subscriber");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-heading text-white">Stay Updated</h2>
          <p className="text-white/50 text-sm">{entries.length} newsletter subscribers</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={fetchNewsletter}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="refresh-newsletter-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={exportToCSV}
            className="bg-brand-peach text-deep-violet hover:bg-brand-peach/90"
            data-testid="export-newsletter-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
        <Input
          placeholder="Search by email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40"
          data-testid="newsletter-search"
        />
      </div>

      {/* Table */}
      <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left px-6 py-4 text-white/60 font-medium text-sm">Email</th>
              <th className="text-left px-6 py-4 text-white/60 font-medium text-sm">Subscribed</th>
              <th className="text-right px-6 py-4 text-white/60 font-medium text-sm">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3} className="px-6 py-12 text-center text-white/50">
                  Loading...
                </td>
              </tr>
            ) : filteredEntries.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-6 py-12 text-center text-white/50">
                  {searchTerm ? "No matching subscribers found" : "No newsletter subscribers yet"}
                </td>
              </tr>
            ) : (
              filteredEntries.map((entry) => (
                <tr key={entry.id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="px-6 py-4">
                    <a href={`mailto:${entry.email}`} className="text-brand-peach hover:underline flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {entry.email}
                    </a>
                  </td>
                  <td className="px-6 py-4 text-white/50 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {new Date(entry.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => deleteEntry(entry.id)}
                      className="text-white/40 hover:text-red-400 transition-colors"
                      data-testid={`delete-newsletter-${entry.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Blog Panel, Links to existing blog management
const BlogPanel = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    navigate("/admin/blog");
  }, [navigate]);

  return (
    <div className="flex items-center justify-center h-64">
      <p className="text-white/50">Redirecting to blog management...</p>
    </div>
  );
};

// Main Admin Dashboard
export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("waitlist");
  const [token, setToken] = useState(null);

  useEffect(() => {
    const storedToken = localStorage.getItem("adminToken");
    const expiresAt = localStorage.getItem("adminTokenExpires");

    if (!storedToken || (expiresAt && new Date(expiresAt) < new Date())) {
      toast.error("Please log in to access the admin dashboard");
      navigate("/admin/login");
      return;
    }

    setToken(storedToken);
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminTokenExpires");
    toast.success("Logged out successfully");
    navigate("/admin/login");
  };

  const handleTabChange = (tab) => {
    if (tab === "blog") {
      navigate("/admin/blog");
    } else {
      setActiveTab(tab);
    }
  };

  if (!token) {
    return null;
  }

  return (
    <div className="flex min-h-screen bg-deep-violet">
      <AdminSidebar 
        activeTab={activeTab} 
        setActiveTab={handleTabChange}
        onLogout={handleLogout}
      />
      
      <main className="flex-1 p-8">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {activeTab === "waitlist" && <WaitlistPanel token={token} />}
          {activeTab === "contacts" && <ContactsPanel token={token} />}
          {activeTab === "newsletter" && <NewsletterPanel token={token} />}
        </motion.div>
      </main>
    </div>
  );
}
